import React from "react";
import {
  Box,
  Flex,
  Image,
  Heading,
  Text,
  Badge,
  Button,
  Icon,
  VStack,
  SimpleGrid,
  Divider,
} from "@chakra-ui/react";
import {
  FaRegClock,
  FaRegUser,
  FaRegClone,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUsers,
  FaClock,
  FaPlay,
  FaCheck,
} from "react-icons/fa";
import MarkdownRenderer from "../../Common/MarkdownRenderer";

const CourseDetailsMainInfo = ({
  image,
  title,
  description,
  tag,
  price,
  classes,
  batch_type,
  duration,
  enrolled,
  deadline,
  rating,
  reviews,
  employees,
  venue,
  startDate,
  encodeId,
  endDate,
  startTime,
  endTime,
  status,
  sessionDays = [],
  seatCapacity,
  eachClassDuration,
  expectedStartingDate,
  speakers = [],
  courseId,
  batchName,
  trainingName,
  courseThumbnail,
  batchStatus,
}) => {
  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Format time from 24-hour to 12-hour AM/PM format
  const formatTime = (timeString) => {
    if (!timeString) return "";

    try {
      let time = timeString.trim();

      if (
        time.toLowerCase().includes("am") ||
        time.toLowerCase().includes("pm")
      ) {
        return time;
      }

      const timeParts = time.split(":");
      if (timeParts.length < 2) return timeString;

      let hours = parseInt(timeParts[0]);
      const minutes = timeParts[1];

      if (isNaN(hours)) return timeString;

      const period = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12;

      return `${hours}:${minutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  // Check if content contains markdown syntax or HTML markup
  const hasMarkdownOrHtml = (content) => {
    if (!content) return false;

    const markdownPatterns = [
      /#{1,6}\s+/, // Headers
      /\*{1,2}[^*]+\*{1,2}/, // Bold/Italic
      /`[^`]+`/, // Inline code
      /```[\s\S]*?```/, // Code blocks
      /\[.+\]\(.+\)/, // Links
      /^[-*+]\s+/m, // Unordered lists
      /^\d+\.\s+/m, // Ordered lists
      /^>\s+/m, // Blockquotes
    ];

    const htmlPatterns = [
      /<p[^>]*>/, // Paragraph tags
      /<h[1-6][^>]*>/, // Header tags
      /<strong[^>]*>/, // Strong tags
      /<em[^>]*>/, // Emphasis tags
      /<i[^>]*>/, // Italic tags
      /<b[^>]*>/, // Bold tags
      /<ul[^>]*>/, // Unordered list tags
      /<ol[^>]*>/, // Ordered list tags
      /<li[^>]*>/, // List item tags
      /<br\s*\/?>/, // Line break tags
      /<div[^>]*>/, // Div tags
      /<span[^>]*>/, // Span tags
    ];

    return (
      markdownPatterns.some((pattern) => pattern.test(content)) ||
      htmlPatterns.some((pattern) => pattern.test(content))
    );
  };

  // Redirect to enrollment function
  const redirectToEnroll = () => {
    const courseInfo = {
      id: encodeId || "",
      batch_name: batchName || title || "",
      start_date: startDate || "",
      end_date: endDate || "",
      total_session_duration: duration || "",
      training_name: trainingName || title || "",
      venue_name: venue || "",
      course_thumbnail: courseThumbnail || image || "",
      batch_status: batchStatus || status || "",
      enrollment_deadline: deadline || "",
    };

    if (courseInfo.id) {
      localStorage.setItem("courseInfo", JSON.stringify(courseInfo));
      window.location.href = `https://ba-systems.com/course/dashboard`;
    } else {
      console.error("Course Info not found!");
    }
  };

  // Get status display
  const getStatusDisplay = (status) => {
    // First, check if we have explicit status from API
    if (status) {
      const statusLower = status.toLowerCase();
      if (
        statusLower === "completed" ||
        statusLower === "finished" ||
        statusLower === "ended"
      ) {
        return { icon: FaCheck, color: "#805AD5", text: "Completed" };
      } else if (
        statusLower === "ongoing" ||
        statusLower === "running" ||
        statusLower === "active"
      ) {
        return { icon: FaPlay, color: "#38A169", text: "Ongoing" };
      } else if (
        statusLower === "upcoming" ||
        statusLower === "scheduled" ||
        statusLower === "pending"
      ) {
        return { icon: FaClock, color: "#3182CE", text: "Upcoming" };
      }
    }

    // Fallback to date-based calculation if no explicit status
    const currentDate = new Date();
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (start && currentDate < start) {
      return { icon: FaClock, color: "#3182CE", text: "Upcoming" };
    } else if (start && end && currentDate >= start && currentDate <= end) {
      return { icon: FaPlay, color: "#38A169", text: "Ongoing" };
    } else if (end && currentDate > end) {
      return { icon: FaCheck, color: "#805AD5", text: "Completed" };
    }

    // Default fallback
    return { icon: FaClock, color: "#3182CE", text: "Upcoming" };
  };

  const statusDisplay = getStatusDisplay(status || batchStatus);

  // Filter speakers that have visible content
  const visibleSpeakers = speakers.filter((speaker) => {
    return (
      (speaker.show_name == 1 && speaker.name) ||
      (speaker.show_designation == 1 && speaker.designation) ||
      (speaker.show_email == 1 && speaker.email) ||
      (speaker.speaker_pfp && speaker.show_pfp)
    );
  });

  // Check if registration deadline has passed
  const isRegistrationDeadlineOver = () => {
    if (!deadline) return false;

    try {
      const currentDate = new Date();
      const deadlineDate = new Date(deadline);

      // Set current date to start of day for fair comparison
      currentDate.setHours(0, 0, 0, 0);
      deadlineDate.setHours(23, 59, 59, 999); // End of deadline day

      return currentDate > deadlineDate;
    } catch (error) {
      console.error("Error parsing deadline date:", error);
      return false;
    }
  };

  const deadlineOver = isRegistrationDeadlineOver();

  return (
    <Box w="100%" maxW="7xl" mx="auto" mt={6}>
      {/* Main Course Info Card */}
      <Flex
        bg="white"
        borderRadius="12px"
        boxShadow="0 4px 12px rgba(0,0,0,0.08)"
        p={{ base: 4, md: 6 }}
        gap={{ base: 4, md: 6 }}
        direction={{ base: "column", md: "row" }}
        align={{ base: "start", md: "center" }}
        mb={6}
      >
        {/* Left: Image */}
        <Box
          minW={{ base: "100%", md: "300px" }}
          maxW={{ base: "100%", md: "300px" }}
          h={{ base: "200px", md: "220px" }}
          position="relative"
        >
          <Image
            src={image}
            alt={title}
            borderRadius="12px"
            w="100%"
            h="100%"
            objectFit="contain"
          />
          <Badge
            position="absolute"
            top={3}
            left={3}
            bg={statusDisplay.color}
            color="white"
            borderRadius="6px"
            px={2}
            py={1}
            fontSize="xs"
            fontWeight="600"
          >
            <Icon as={statusDisplay.icon} mr={1} />
            {status}
          </Badge>
        </Box>

        {/* Right: Course Info */}
        <Box flex="1" w="100%">
          <Heading
            fontSize={{ base: "lg", md: "2xl" }}
            fontWeight="bold"
            color="#1A202C"
            mb={1}
            lineHeight={1.2}
          >
            {title}
          </Heading>

          {hasMarkdownOrHtml(description) ? (
            <Box color="#7A869A" fontSize="md" mb={3} lineHeight={1.4}>
              <MarkdownRenderer content={description} />
            </Box>
          ) : (
            <Text color="#7A869A" fontSize="md" mb={3} lineHeight={1.4}>
              {description}
            </Text>
          )}

          {/* Quick Info Grid */}
          <SimpleGrid columns={{ base: 3, md: 5 }} spacing={3} mb={3}>
            <Flex align="center" gap={2}>
              <Icon as={FaRegClone} color="#235B63" />
              <Box>
                <Text fontSize="xs" color="#7A869A" lineHeight={1.2}>
                  Classes
                </Text>
                <Text fontSize="sm" fontWeight="600" lineHeight={1.3}>
                  {classes}
                </Text>
              </Box>
            </Flex>
            <Flex align="center" gap={2}>
              <Icon as={FaRegClock} color="#235B63" />
              <Box>
                <Text fontSize="xs" color="#7A869A" lineHeight={1.2}>
                  Duration
                </Text>
                <Text fontSize="sm" fontWeight="600" lineHeight={1.3}>
                  {duration}
                </Text>
              </Box>
            </Flex>
            <Flex align="center" gap={2}>
              <Icon as={FaUsers} color="#235B63" />
              <Box>
                <Text fontSize="xs" color="#7A869A" lineHeight={1.2}>
                  Capacity
                </Text>
                <Text fontSize="sm" fontWeight="600" lineHeight={1.3}>
                  {seatCapacity}
                </Text>
              </Box>
            </Flex>
            <Flex align="center" gap={2}>
              <Icon as={FaUsers} color="#235B63" />
              <Box>
                <Text fontSize="xs" color="#7A869A" lineHeight={1.2}>
                  Registration Fee
                </Text>
                <Text 
                  fontSize="sm" 
                  fontWeight="600" 
                  lineHeight={1.3}
                  bg={
                    batch_type === "Free" 
                      ? "#91f0dd" 
                      : "#FFF5E6"
                  }
                  px={2}
                  py={1}
                  borderRadius="4px"
                  display="inline-block"
                  w="fit-content"
                >
                  {batch_type === "Free" ? "Free" : price || "TBA"}
                </Text>
              </Box>
            </Flex>
            <Flex align="center" gap={2}>
              <Icon as={FaMapMarkerAlt} color="#235B63" />
              <Box>
                <Text fontSize="xs" color="#7A869A" lineHeight={1.2}>
                  Venue
                </Text>
                <Text fontSize="sm" fontWeight="600" lineHeight={1.3}>
                  {venue}
                </Text>
              </Box>
            </Flex>
          </SimpleGrid>

          <Flex gap={3} flexWrap="wrap">
            {/* Conditional button rendering based on status and deadline */}
            {deadlineOver ? (
              <Button
                colorScheme="red"
                size="md"
                borderRadius="8px"
                px={6}
                isDisabled={true}
                bg="#FED7D7"
                color="#C53030"
                cursor="not-allowed"
              >
                <Icon as={FaClock} mr={2} />
                Registration Closed
              </Button>
            ) : (
              <Button
                colorScheme="green"
                size="md"
                borderRadius="8px"
                px={6}
                onClick={redirectToEnroll}
              >
                Apply Now
              </Button>
            )}
          </Flex>
        </Box>
      </Flex>

      {/* Detailed Information Cards */}
      {/* Schedule Information */}
      <Box
        bg="white"
        borderRadius="12px"
        boxShadow="0 2px 8px rgba(0,0,0,0.06)"
        p={5}
      >
        <Heading
          fontSize="lg"
          fontWeight="bold"
          color="#1A202C"
          mb={3}
          lineHeight={1.3}
        >
          <Icon as={FaCalendarAlt} mr={2} color="#3182CE" />
          Schedule Information
        </Heading>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {/* First Column */}
          <VStack align="start" spacing={4}>
            <Box>
              <Text fontSize="sm" color="#7A869A" mb={0.5} lineHeight={1.2}>
                Course Duration
              </Text>
              <Text fontWeight="600" lineHeight={1.3}>
                {formatDate(startDate)} - {formatDate(endDate)}
              </Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="#7A869A" mb={0.5} lineHeight={1.2}>
                Class Time
              </Text>
              <Text fontWeight="600" lineHeight={1.3}>
                {formatTime(startTime)} - {formatTime(endTime)}
              </Text>
            </Box>
          </VStack>

          {/* Second Column */}
          <VStack align="start" spacing={4}>
            <Box>
              <Text fontSize="sm" color="#7A869A" mb={0.5} lineHeight={1.2}>
                Each Class Duration
              </Text>
              <Text fontWeight="600" lineHeight={1.3}>
                {eachClassDuration}
              </Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="#7A869A" mb={0.5} lineHeight={1.2}>
                Session Days
              </Text>
              <Text fontWeight="600" lineHeight={1.3}>
                {sessionDays.join(", ")}
              </Text>
            </Box>
          </VStack>

          {/* Third Column */}
          <VStack align="start" spacing={4}>
            <Box>
              <Text fontSize="sm" color="#7A869A" mb={0.5} lineHeight={1.2}>
                Expected Starting Date
              </Text>
              <Text fontWeight="600" lineHeight={1.3}>
                {formatDate(expectedStartingDate)}
              </Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="#7A869A" mb={0.5} lineHeight={1.2}>
                Registration Deadline
              </Text>
              <Text fontWeight="600" color="#E53E3E" lineHeight={1.3}>
                {formatDate(deadline)}
              </Text>
            </Box>
          </VStack>
        </SimpleGrid>
      </Box>

      {/* </SimpleGrid> */}
      <Box
        bg="white"
        borderRadius="12px"
        boxShadow="0 2px 8px rgba(0,0,0,0.06)"
        mt={6}
        p={5}
      >
        <Heading
          fontSize="lg"
          fontWeight="bold"
          color="#1A202C"
          mb={3}
          lineHeight={1.3}
        >
          <Icon as={FaRegUser} mr={2} color="#38A169" />
          Instructors ({visibleSpeakers.length})
        </Heading>

        {visibleSpeakers.length > 0 ? (
          <VStack spacing={3}>
            {visibleSpeakers.map((speaker, index) => (
              <Box key={speaker.id || index} w="100%">
                <Flex align="start" gap={4}>
                  {speaker.speaker_pfp && speaker.show_pfp && (
                    <Image
                      src={speaker.speaker_pfp}
                      alt={speaker.name}
                      borderRadius="full"
                      boxSize="70px"
                      objectFit="contain"
                      flexShrink={0}
                    />
                  )}
                  <VStack align="start" spacing={1} flex="1">
                    {speaker.show_name == 1 && speaker.name && (
                      <Text
                        fontWeight="bold"
                        fontSize="md"
                        color="#1A202C"
                        lineHeight={1.3}
                      >
                        {speaker.name}
                      </Text>
                    )}
                    {speaker.show_designation == 1 && speaker.designation && (
                      <Text color="#7A869A" fontSize="sm" lineHeight={1.3}>
                        {speaker.designation}
                        {speaker.show_organization &&
                          speaker.organization &&
                          ` at ${speaker.organization}`}
                      </Text>
                    )}
                    {speaker.show_email == 1 && speaker.email && (
                      <Text color="#3182CE" fontSize="sm" lineHeight={1.3}>
                        {speaker.email}
                      </Text>
                    )}
                  </VStack>
                </Flex>
                {index < visibleSpeakers.length - 1 && <Divider mt={2} />}
              </Box>
            ))}
          </VStack>
        ) : (
          <Text color="#7A869A" fontSize="sm" lineHeight={1.3}>
            No instructor information available
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default CourseDetailsMainInfo;
